// SimpleSelectField.tsx
"use client";

import * as React from "react";
import { Field, Box } from "@chakra-ui/react";
import { Select, createListCollection } from "@chakra-ui/react";
import useControllable from "hooks/useControllable/useControllable";

export interface SelectOption {
  label: string;
  value: string;
}

export interface SimpleSelectFieldProps {
  id: string;
  label?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
}

export const SelectField: React.FC<SimpleSelectFieldProps> = (props) => {
  const { id, label, placeholder, required, helperText, error, options } =
    props;

  const [value, setValue] = useControllable<string>({
    value: props.value,
    defaultValue: props.defaultValue ?? "",
    onChange: props.onChange,
  });

  const collection = React.useMemo(
    () =>
      createListCollection({
        items: options,
      }),
    [options],
  );
  const valueArray = React.useMemo(() => (value ? [value] : []), [value]);
  return (
    <Field.Root invalid={!!error} required={!!required}>
      {label && (
        <Field.Label>
          {label}
          {required && <Field.RequiredIndicator />}
        </Field.Label>
      )}

      <Box w="full">
        <Select.Root
          collection={collection}
          value={valueArray}
          onValueChange={(details: any) =>
            setValue(details?.value ?? details ?? "")
          }
          width="full"
        >
          <Select.HiddenSelect name={id} />
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText
                placeholder={placeholder ?? "Select"}
              ></Select.ValueText>
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>

          <Select.Positioner>
            <Select.Content>
              {collection.items.map((item: any) => (
                <Select.Item key={item.value} item={item}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </Box>

      {error ? (
        <Field.ErrorText>{error}</Field.ErrorText>
      ) : helperText ? (
        <Field.HelperText>{helperText}</Field.HelperText>
      ) : null}
    </Field.Root>
  );
};

export default SelectField;
